import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, type Category, type LineItem, type ReportDetail } from '../lib/api';
import { useAuth, canReview } from '../lib/auth-context';
import { ErrorNote, Money, Sheet, Spinner, StatusPill } from '../components/ui';
import { ReceiptCapture } from '../components/ReceiptCapture';
import { ReceiptList } from '../components/ReceiptList';
import { LineSheet } from './LineSheet';
import { MileageSheet } from './MileageSheet';
import { PerDiemSheet } from './PerDiemSheet';
import { PerDiemDaySheet } from './PerDiemDaySheet';

export function ReportDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [data, setData] = useState<ReportDetail | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [lineOpen, setLineOpen] = useState(false);
  const [editing, setEditing] = useState<LineItem | null>(null);
  const [pendingReceiptId, setPendingReceiptId] = useState<string | null>(null);
  const [mileageOpen, setMileageOpen] = useState(false);
  const [editingMileage, setEditingMileage] = useState<LineItem | null>(null);
  const [perDiemOpen, setPerDiemOpen] = useState(false);
  const [editingPerDiemDay, setEditingPerDiemDay] = useState<LineItem | null>(null);
  /**
   * Bumped every time an editor is opened, and used as its key.
   *
   * These sheets stay mounted while closed, so their useState initialisers
   * run once. Keying on the edited row is not enough: two new expenses in a
   * row share the key "new", and the second opened holding the first's
   * values. Resetting in an effect instead would clear them one render late,
   * painting the old data first -- a visible flash on a phone. A changing key
   * remounts, so the first paint is already correct.
   */
  const [editorGeneration, setEditorGeneration] = useState(0);
  const nextEditor = () => setEditorGeneration((n) => n + 1);
  const [decisionOpen, setDecisionOpen] = useState<null | 'reject' | 'request-changes' | 'approve'>(null);
  const [note, setNote] = useState('');
  const [previews, setPreviews] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    try {
      const detail = await api.get<ReportDetail>(`/reports/${id}`);
      setData(detail);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the report');
    }
  }, [id]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    api.get<{ categories: Category[] }>('/categories').then((d) => setCategories(d.categories)).catch(() => {});
  }, []);

  if (error && !data) return <ErrorNote>{error}</ErrorNote>;
  if (!data) return <Spinner label="Loading the report" />;

  const { report, trip, lineItems, receipts, total, submitBlockers, canEdit } = data;

  // The gate already works out which lines are short a receipt; reuse that
  // rather than re-deriving the rule in the UI and risking the two disagreeing.
  const blockedLineIds = new Set(
    submitBlockers.map((b) => b.lineItemId).filter((id): id is string => Boolean(id)),
  );
  const isOwner = report.userId === user?.id;
  const reviewer = canReview(user?.role);
  const isTravel = report.type === 'travel';

  /**
   * Every line is editable, but each kind needs its own editor: a mileage line
   * has no merchant, and a per-diem day has meal toggles rather than an
   * amount. Routing here keeps that decision in one place.
   */
  function openEditor(line: LineItem) {
    nextEditor();
    setPendingReceiptId(null);
    if (line.kind === 'mileage') {
      setEditingMileage(line);
      setMileageOpen(true);
    } else if (line.kind === 'per_diem') {
      setEditingPerDiemDay(line);
    } else {
      setEditing(line);
      setLineOpen(true);
    }
  }

  async function act(path: string, body?: unknown) {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/reports/${id}${path}`, body ?? {});
      await load();
      setDecisionOpen(null);
      setNote('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That action failed');
    } finally {
      setBusy(false);
    }
  }

  async function downloadPdf() {
    setBusy(true);
    try {
      const { exportId } = await api.post<{ exportId: string }>(`/reports/${id}/exports`, { includeReceipts: true });
      const { downloadUrl } = await api.get<{ downloadUrl: string | null }>(`/exports/${exportId}`);
      if (downloadUrl) window.location.href = downloadUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The PDF could not be generated');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pb-4">
      <header className="sticky top-0 z-20 -mx-4 mb-4 border-b border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="truncate text-lg font-semibold">{report.title}</h1>
            <p className="mt-0.5 text-sm text-slate-500">
              {isTravel ? `${trip?.destination} · ${trip?.startDate} – ${trip?.endDate}`
                        : report.periodMonth?.slice(0, 7)}
              {reviewer && !isOwner ? ` · ${data.owner?.fullName}` : ''}
            </p>
          </div>
          <div className="text-right">
            <Money value={total} className="text-xl font-semibold" />
            <div className="mt-1"><StatusPill status={report.status} /></div>
          </div>
        </div>
      </header>

      <ErrorNote>{error}</ErrorNote>

      {report.decisionNote ? (
        <div className="card mb-4 border-l-4 border-l-amber-400 p-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Note from finance</p>
          <p className="mt-1 text-sm">{report.decisionNote}</p>
        </div>
      ) : null}

      <div className="lg:grid lg:grid-cols-3 lg:gap-6">
        <div className="lg:col-span-2">
          {lineItems.length === 0 ? (
            <div className="card p-6 text-center text-slate-500">
              Nothing on this report yet. Add a receipt or an expense below.
            </div>
          ) : (
            <ul className="space-y-2">
              {lineItems.map((line) => (
                <li key={line.id}>
                  <button
                    type="button"
                    disabled={!canEdit || !isOwner}
                    onClick={() => openEditor(line)}
                    className="card flex w-full items-center gap-3 p-3 text-left disabled:opacity-100"
                  >
                    {line.receipt_id && previews[line.receipt_id] ? (
                      <img src={previews[line.receipt_id]} alt="" className="h-16 w-16 rounded object-cover" />
                    ) : (
                      <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded bg-slate-100 text-xs text-slate-400">
                        {line.kind === 'mileage' ? 'mi' : line.kind === 'per_diem' ? 'diem' : 'no img'}
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {line.merchant || line.description || line.category_name}
                      </p>
                      <p className="text-sm text-slate-500">
                        {line.category_name} · {line.expense_date}
                      </p>
                      {blockedLineIds.has(line.id) && !line.receipt_id && line.kind === 'expense' ? (
                        <span className="mt-1 inline-block rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                          Needs a receipt — tap to add
                        </span>
                      ) : null}
                      {line.kind === 'mileage' && line.miles ? (
                        <p className="text-xs text-slate-400">
                          {line.miles} mi × ${line.rate_per_mile}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <Money value={line.amount} className="font-semibold" />
                      {canEdit && isOwner ? (
                        <span aria-hidden className="text-lg leading-none text-slate-300">›</span>
                      ) : null}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {submitBlockers.length > 0 && canEdit && isOwner ? (
            <div className="card mt-4 border-l-4 border-l-amber-400 p-3">
              <p className="text-sm font-medium">Before this can be submitted:</p>
              <ul className="mt-1 list-disc pl-5 text-sm text-slate-600">
                {submitBlockers.map((b, i) => <li key={i}>{b.message}</li>)}
              </ul>
            </div>
          ) : null}
        </div>

        <aside className="mt-6 lg:mt-0">
          <div className="card p-4">
            <h2 className="mb-2 text-sm font-semibold">Receipts ({receipts.length})</h2>
            {canEdit && isOwner ? (
              <ReceiptCapture
                reportId={report.id}
                onUploaded={({ receiptId, previewUrl }) => {
                  setPreviews((p) => ({ ...p, [receiptId]: previewUrl }));
                  // Open the line sheet straight away with today's date and
                  // the receipt already attached: four taps and one number.
                  nextEditor();
                  setPendingReceiptId(receiptId);
                  setEditing(null);
                  setLineOpen(true);
                  void load();
                }}
              />
            ) : null}
            <ReceiptList
              receipts={receipts}
              lineItems={lineItems}
              canEdit={canEdit && isOwner}
              onChanged={() => void load()}
            />
          </div>
        </aside>
      </div>

      {/* Sticky action bar, safe-area padded. */}
      {canEdit && isOwner ? (
        <div className="action-bar -mx-4 mt-6">
          {submitBlockers.length === 0 && lineItems.length > 0 ? (
            <button type="button" className="btn-primary mb-2 w-full" disabled={busy}
                    onClick={() => void act('/submit')}>
              Submit for approval
            </button>
          ) : null}
          <div className={`grid gap-2 ${isTravel ? 'grid-cols-3' : 'grid-cols-2'}`}>
            <button type="button" className="btn-secondary"
                    onClick={() => { nextEditor(); setEditing(null); setPendingReceiptId(null); setLineOpen(true); }}>
              Add expense
            </button>
            <button type="button" className="btn-secondary"
                    onClick={() => { nextEditor(); setEditingMileage(null); setMileageOpen(true); }}>Mileage</button>
            {isTravel ? (
              <button type="button" className="btn-secondary" onClick={() => setPerDiemOpen(true)}>Per diem</button>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Finance decisions. */}
      {reviewer && report.status === 'submitted' ? (
        <div className="action-bar -mx-4 mt-6 grid grid-cols-3 gap-2">
          <button type="button" className="btn-primary" disabled={busy} onClick={() => void act('/approve')}>
            Approve
          </button>
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => setDecisionOpen('request-changes')}>
            Changes
          </button>
          <button type="button" className="btn-danger" disabled={busy} onClick={() => setDecisionOpen('reject')}>
            Reject
          </button>
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-2">
        <button type="button" className="btn-secondary" disabled={busy} onClick={() => void downloadPdf()}>
          {busy ? 'Working…' : 'Download PDF'}
        </button>
        {reviewer && report.status === 'approved' ? (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => void act('/mark-paid')}>
            Mark paid
          </button>
        ) : null}
        {isOwner && report.status === 'submitted' ? (
          <button type="button" className="btn-secondary" disabled={busy} onClick={() => void act('/recall')}>
            Recall
          </button>
        ) : null}
        <button type="button" className="btn-secondary" onClick={() => navigate(-1)}>Back</button>
      </div>

      <LineSheet
        key={`line-${editorGeneration}`}
        open={lineOpen}
        onClose={() => { setLineOpen(false); setEditing(null); setPendingReceiptId(null); }}
        reportId={report.id}
        categories={categories}
        receipts={receipts}
        existing={editing}
        attachReceiptId={pendingReceiptId}
        onSaved={() => void load()}
      />
      <MileageSheet
        key={`mileage-${editorGeneration}`}
        open={mileageOpen}
        onClose={() => { setMileageOpen(false); setEditingMileage(null); }}
        reportId={report.id}
        categories={categories}
        existing={editingMileage}
        onSaved={() => void load()}
      />
      <PerDiemDaySheet
        open={editingPerDiemDay !== null}
        onClose={() => setEditingPerDiemDay(null)}
        line={editingPerDiemDay}
        onSaved={() => void load()}
      />
      <PerDiemSheet open={perDiemOpen} onClose={() => setPerDiemOpen(false)} reportId={report.id}
                    categories={categories} onSaved={() => void load()} />

      <Sheet
        open={decisionOpen === 'reject' || decisionOpen === 'request-changes'}
        onClose={() => setDecisionOpen(null)}
        title={decisionOpen === 'reject' ? 'Reject this report' : 'Request changes'}
        footer={
          <button type="button" className="btn-primary w-full" disabled={busy || !note.trim()}
                  onClick={() => void act(`/${decisionOpen}`, { note })}>
            {busy ? 'Sending…' : 'Send'}
          </button>
        }
      >
        <label className="label" htmlFor="note">
          Note to the submitter (required — they need to know what to fix)
        </label>
        <textarea id="note" className="field" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
      </Sheet>
    </div>
  );
}
